import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Wrench, Phone, Car, DollarSign, Clock, ChevronRight } from 'lucide-react';
import { vehicleService } from '../../services/vehicleService';
import { formatVehicleTitle } from '../../utils/textFormatting';

const VolkswagenInventory = () => {
  const [vwVehicles, setVwVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadVwInventory = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await vehicleService.getAvailableVehicles();
        if (!error && data) {
          const filtered = data.filter(v => {
            const make = (v.make || '').toLowerCase();
            return make === 'volkswagen' || make === 'vw';
          });
          setVwVehicles(filtered);
        }
      } catch (err) {
        console.error('Error loading VW inventory:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadVwInventory();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Used Volkswagen for Sale Dallas-Fort Worth - Pre-Owned VW Inventory</title>
        <meta name="description" content="Shop pre-owned Volkswagens in DFW. Every VW inspected by German car specialists before it hits the lot. Jetta, GTI, Tiguan and more. Financing available. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/inventory/volkswagen" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/inventory/volkswagen" />
        <meta property="og:title" content="Pre-Owned Volkswagen Inventory - Paradise, TX" />
        <meta property="og:description" content="German car mechanics selling German cars. We know what to look for and what to avoid. Browse our VW inventory in Paradise, TX." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/vw-repair.webp" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pre-Owned Volkswagen Inventory - Paradise, TX" />
        <meta name="twitter:description" content="German car mechanics selling German cars. We know what to look for and what to avoid. Browse our VW inventory in Paradise, TX." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/vw-repair.webp" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "name": "Paradise-Performance LLC",
            "description": "Pre-owned Volkswagen vehicles inspected by German car specialists",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "brand": "Volkswagen"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Are used Volkswagens reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Modern Volkswagens (2015+) rank well in long-term reliability studies. The 2.0T EA888 engine and MQB platform are proven across millions of vehicles worldwide. Common trouble spots like water pumps and timing chains have been revised in newer models. A well-maintained VW will run 200,000+ miles without major issues."
                }
              },
              {
                "@type": "Question",
                "name": "What should I look for when buying a used Volkswagen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Check the DSG transmission service history (fluid and filter every 40,000 miles). Look for timing chain tensioner updates on TSI engines. Verify the water pump has been replaced or is the updated design. Check for carbon buildup symptoms on direct injection models. We inspect all of this before listing any VW."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer financing on used Volkswagens?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. We offer in-house financing on select vehicles. Fill out our online finance application to get started. Approval typically takes less than 24 hours."
                }
              },
              {
                "@type": "Question",
                "name": "Why buy a used Volkswagen from a repair shop?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We work on Volkswagens every day. Our technicians know exactly what breaks, what wears out and what to inspect. Every VW we sell goes through a multi-point inspection by the same mechanics who repair them. You get a vehicle vetted by specialists, not just a quick detail and a price tag."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 pt-24 lg:pt-36 md:pt-16 sm:pt-20 pb-20"
      >
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#001E50] to-[#003580] text-white py-16 mb-12 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="md:flex items-center gap-8 max-w-6xl mx-auto">
              <div className="flex-1 text-center md:text-left mb-8 md:mb-0">
                <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-3">Paradise-Performance LLC</p>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">Pre-Owned Volkswagens Inspected by VW Specialists</h1>
                <p className="text-xl text-blue-100 mb-8 max-w-xl">
                  Most dealers run a quick scan and call it good. We tear into these cars the same way we would for a paying repair customer. If it doesn't pass our shop's standards, it doesn't make the lot.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <a
                    href="#inventory"
                    className="bg-white text-[#001E50] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
                  >
                    Browse VW Inventory
                  </a>
                  <a
                    href="tel:8173822316"
                    className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
                  >
                    Call (817) 382-2316
                  </a>
                </div>
              </div>
              <div className="flex-shrink-0 w-full md:w-[400px] flex items-center justify-center">
                <img
                  src="/assets/assets/images/vw-black2.webp"
                  alt="Volkswagen Badge"
                  className="w-full max-w-[380px] rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          {/* Why Buy a VW From Us */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">German Car Mechanics Selling German Cars</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Here's the problem with buying a used Volkswagen from a typical lot. The sales guy knows how to talk about monthly payments. He doesn't know what a failing timing chain tensioner sounds like. He can't tell you whether the DSG fluid was changed at 40,000 miles or never. He sees a clean interior and a car that starts. That's his inspection.
                </p>
                <p>
                  We're different because we're not salespeople who happen to have a lot. We're mechanics who happen to sell cars. Our shop works on Volkswagens every single day. GTIs with boost leaks. Jettas with check engine lights. Tiguans that need water pumps. We know these cars inside and out because we fix them for a living.
                </p>
                <p>
                  When a VW comes through our doors as potential inventory, it gets the same treatment as a customer's car. Full diagnostic scan with VCDS. Compression test if the mileage warrants it. We check the timing chain stretch, the DSG adaptation values and the coolant system. If something's wrong, we fix it before listing it. If it's not worth fixing, we don't list it at all.
                </p>
                <p>
                  That's not a sales pitch. That's just how we run things. You're buying from the same people who can service your VW for years after the sale. We're not trying to move iron and forget your name. We want you coming back for oil changes and brake jobs. That only works if we sell you something solid.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Inspection Process */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Check Before a VW Hits the Lot</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-[#001E50]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Engine and Drivetrain</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Full VCDS diagnostic scan and fault code review</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Timing chain tensioner condition and stretch measurement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> DSG transmission adaptation values and fluid condition</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Turbo boost pressure test and wastegate operation</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Oil consumption check over test drive period</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#001E50]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Safety Systems</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Brake pad and rotor thickness measurement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Suspension component inspection (control arms and bushings)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Tire tread depth and wear pattern analysis</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> ABS and ESC system function test</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> All lighting and electrical system verification</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Car className="w-6 h-6 text-[#001E50]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">VW-Specific Trouble Spots</h3>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Water pump and thermostat housing (common failure point)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> PCV valve and intake carbon buildup assessment</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Window regulator operation (all four doors)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Ignition coil and spark plug condition</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Coolant flange and hose inspection for leaks</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* VW Models Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Volkswagen Models We Typically Stock</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  We don't limit ourselves to one model. If a solid VW comes through at the right price, we'll pick it up. That said, certain models show up on our lot more often than others.
                </p>
                <p>
                  <strong>Jetta</strong> — The workhorse of the VW lineup. These things are everywhere in DFW and for good reason. Solid fuel economy. Comfortable ride. The 1.4T and 2.0T engines are both proven. We see Jettas last well past 150,000 miles when they're maintained.
                </p>
                <p>
                  <strong>GTI</strong> — The hot hatch that punches way above its weight class. Quick, practical and surprisingly affordable used. We check the turbo system and DSG extra carefully on these since they tend to get driven hard. Nothing wrong with that. We just make sure the car can handle it.
                </p>
                <p>
                  <strong>Tiguan</strong> — VW's answer to the compact SUV market. The second-gen Tiguan (2018+) is a solid family hauler. Good cargo space and AWD available. We pay special attention to the 4MOTION system and transfer case on all-wheel-drive models.
                </p>
                <p>
                  <strong>Golf and Golf R</strong> — The Golf is the Swiss Army knife of cars. Hatchback versatility in a compact package. The Golf R is a different animal entirely — 315 horsepower through all four wheels. We see a lot of enthusiast-owned Rs come through and we love getting our hands on them.
                </p>
                <p>
                  <strong>Atlas and Atlas Cross Sport</strong> — VW's three-row SUV for bigger families. The VR6 engine is buttery smooth. These are newer to the used market but we're starting to see more of them at prices that make sense.
                </p>
                <p>
                  <strong>Passat</strong> — The mid-size sedan that nobody talks about but everybody should. More rear legroom than a Camry or Accord. The turbo four puts out enough power to merge without white-knuckling it and still returns solid highway mileage. People cross-shop these against base-model BMWs and C-Class Mercedes — you get most of the driving feel at half the long-term cost. Quiet cabin. Comfortable seats. Just a good car that doesn't need a billboard to prove it.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Live Inventory Section */}
          <div id="inventory">
            <motion.div {...fadeInUp} className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Volkswagens Currently in Stock</h2>
              <p className="text-lg text-gray-600 text-center mb-8 max-w-3xl mx-auto">
                Every VW listed below passed our multi-point inspection. What you see is what's on the lot right now.
              </p>

              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#001E50] mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading Volkswagen inventory...</p>
                </div>
              ) : vwVehicles.length > 0 ? (
                <>
                  <style>{`
                    .vw-inv-card {
                      position: relative;
                      background: linear-gradient(135deg, #e0e0e0, #c0c0c0);
                      border-radius: 16px;
                      box-shadow:
                        4px 4px 8px rgba(0, 0, 0, 0.2),
                        -4px -4px 8px rgba(255, 255, 255, 0.2),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.3),
                        inset -1px -1px 2px rgba(0, 0, 0, 0.3);
                      transition: transform 0.3s ease, box-shadow 0.3s ease;
                      overflow: hidden;
                      display: flex;
                      flex-direction: column;
                    }
                    @media (min-width: 768px) {
                      .vw-inv-card {
                        border-radius: 24px;
                        box-shadow:
                          6px 6px 12px rgba(0, 0, 0, 0.3),
                          -6px -6px 12px rgba(255, 255, 255, 0.3),
                          inset 2px 2px 4px rgba(255, 255, 255, 0.4),
                          inset -2px -2px 4px rgba(0, 0, 0, 0.5);
                      }
                    }
                    .vw-inv-card::before {
                      content: "";
                      position: absolute;
                      top: 0; left: 0; width: 100%; height: 100%;
                      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.7), transparent);
                      background-size: 200% 200%;
                      animation: vwinvshine 3s infinite ease-in-out;
                      border-radius: inherit;
                      pointer-events: none;
                      z-index: 1;
                    }
                    .vw-inv-card:hover {
                      transform: translateY(-5px);
                      box-shadow:
                        8px 8px 16px rgba(0, 0, 0, 0.35),
                        -8px -8px 16px rgba(255, 255, 255, 0.35),
                        inset 2px 2px 4px rgba(255, 255, 255, 0.5),
                        inset -2px -2px 4px rgba(0, 0, 0, 0.6);
                    }
                    .vw-inv-content {
                      position: relative;
                      z-index: 2;
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                    }
                    .vw-inv-img {
                      position: relative;
                      border-radius: 16px 16px 0 0;
                      overflow: hidden;
                      background: linear-gradient(135deg, #f0f0f0, #d0d0d0);
                      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
                    }
                    @media (min-width: 768px) {
                      .vw-inv-img {
                        border-radius: 24px 24px 0 0;
                      }
                    }
                    .vw-inv-btn {
                      background: linear-gradient(135deg, #001E50, #003580);
                      box-shadow:
                        2px 2px 4px rgba(0, 0, 0, 0.3),
                        -2px -2px 4px rgba(255, 255, 255, 0.3),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.3);
                      border: none;
                      border-radius: 12px;
                      transition: all 0.3s ease;
                    }
                    .vw-inv-btn:hover {
                      background: linear-gradient(135deg, #002E70, #004590);
                      box-shadow:
                        3px 3px 6px rgba(0, 0, 0, 0.35),
                        -3px -3px 6px rgba(255, 255, 255, 0.35),
                        inset 1px 1px 2px rgba(255, 255, 255, 0.4);
                      transform: translateY(-1px);
                    }
                    @keyframes vwinvshine {
                      0% { background-position: 0% 50%; }
                      50% { background-position: 100% 50%; }
                      100% { background-position: 0% 50%; }
                    }
                  `}</style>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vwVehicles.map((vehicle) => (
                      <motion.div
                        key={vehicle.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="vw-inv-card"
                      >
                        <div className="vw-inv-content">
                          <div className="vw-inv-img">
                            <img
                              src={vehicle.images?.[0] || '/assets/assets/images/vw-repair.webp'}
                              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              className="w-full h-52 object-cover"
                              onError={(e) => { e.target.src = '/assets/assets/images/vw-repair.webp'; }}
                            />
                            <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                                vehicle.condition === 'new' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                                'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
                              }`}>
                                {vehicle.condition === 'new' ? 'New' : 'Used'}
                              </span>
                              {vehicle.status === 'on-lot' && (
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-green-600 text-white shadow-lg">
                                  Available
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-5 flex flex-col flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="text-lg font-bold text-gray-800">{formatVehicleTitle(vehicle)}</h3>
                                {vehicle.stockNumber && (
                                  <p className="text-sm text-gray-500">Stock #{vehicle.stockNumber}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold bg-gradient-to-br from-blue-700 to-blue-900 bg-clip-text text-transparent">
                                  {formatPrice(vehicle.price)}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center text-sm text-gray-600 mb-3 space-x-3">
                              {vehicle.mileage > 0 && <span>{formatMileage(vehicle.mileage)} mi</span>}
                              {vehicle.mileage > 0 && vehicle.transmission && <span>•</span>}
                              {vehicle.transmission && <span>{vehicle.transmission}</span>}
                              {vehicle.fuelType && <><span>•</span><span>{vehicle.fuelType}</span></>}
                            </div>

                            {vehicle.features && vehicle.features.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {vehicle.features.slice(0, 3).map((feature, index) => (
                                  <span key={index} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-md font-medium">
                                    {feature}
                                  </span>
                                ))}
                                {vehicle.features.length > 3 && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md font-bold">
                                    +{vehicle.features.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex space-x-2 mt-auto">
                              <Link
                                to={`/vehicle/${vehicle.id}`}
                                className="flex-1 vw-inv-btn text-white px-4 py-3 font-bold text-center text-sm"
                              >
                                View Details
                              </Link>
                              <a
                                href="tel:8173822316"
                                className="vw-inv-btn text-white px-4 py-3 font-bold flex items-center justify-center"
                                title="Call about this VW"
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="text-center mt-8">
                    <Link
                      to="/inventory"
                      className="inline-block bg-gray-200 text-gray-800 px-8 py-3 rounded-lg font-bold hover:bg-gray-300 transition"
                    >
                      Browse Full Inventory →
                    </Link>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-2xl mx-auto">
                  <Car className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">No Volkswagens in Stock Right Now</h3>
                  <p className="text-gray-600 mb-6">
                    Our VW inventory moves fast. Call us and let us know what you're looking for. We source vehicles weekly and can keep an eye out for the right one.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="tel:8173822316" className="bg-[#001E50] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#002E70] transition">
                      Call (817) 382-2316
                    </a>
                    <Link to="/inventory" className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition">
                      Browse All Inventory
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Why Buy From a Repair Shop */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">The Advantage of Buying From a Repair Shop</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Think about this for a second. A normal dealership has sales guys, finance guys and maybe a couple technicians in the back. The sales team picks the inventory based on auction prices and what sells fast. The tech does a quick once-over. Everybody's under pressure to move units.
                </p>
                <p>
                  Our shop doesn't work like that. We're a full-service European repair facility first. Cars are our trade, not just our product. When we source a VW, the same techs who fix customer cars all week are the ones inspecting it. They're not rushing through a 15-point checklist. They're running the same diagnostics they'd run on a $2,000 repair job.
                </p>
                <p>
                  After the sale, you're not a stranger. You already know where to bring the car for service. You already know the guys who worked on it. That relationship is worth more than a handshake and a set of floor mats from a franchise lot.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Financing CTA */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 max-w-4xl mx-auto">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Financing Available on Select Volkswagens</h2>
                  <p className="text-gray-600 mb-4">
                    We offer in-house financing on qualifying vehicles. The application takes about five minutes. Most approvals come back within 24 hours.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><DollarSign className="w-4 h-4 text-green-500" /> Competitive rates</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-blue-500" /> Quick approval</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> No obligation</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/finance-application"
                    className="inline-block bg-[#001E50] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#002E70] transition"
                  >
                    Apply for Financing
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Questions About Buying a Used VW</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Are used Volkswagens reliable?</h3>
                <p className="text-gray-700">
                  Yes. Modern Volkswagens (2015 and newer) rank well in long-term reliability studies. The 2.0T EA888 engine and MQB platform are proven across millions of vehicles worldwide. Common trouble spots like water pumps and timing chains have been revised in newer models. A well-maintained VW will run 200,000+ miles without major issues.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">What should I look for when buying a used Volkswagen?</h3>
                <p className="text-gray-700">
                  Check the DSG transmission service history — fluid and filter should be done every 40,000 miles. Look for timing chain tensioner updates on TSI engines. Verify the water pump has been replaced or is the updated composite design. Check for carbon buildup symptoms on direct injection models. We inspect all of this before listing any VW.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Do you offer financing on used Volkswagens?</h3>
                <p className="text-gray-700">
                  Yes. We offer in-house financing on select vehicles. Fill out our <Link to="/finance-application" className="text-[#001E50] font-semibold hover:underline">online finance application</Link> to get started. Approval typically takes less than 24 hours.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Why buy a used Volkswagen from a repair shop?</h3>
                <p className="text-gray-700">
                  We work on Volkswagens every day. Our technicians know exactly what breaks, what wears out and what to inspect. Every VW we sell goes through a multi-point inspection by the same mechanics who repair them. You get a vehicle vetted by specialists, not just a quick detail and a price tag.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Cross-link to VW Repair */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 md:p-12 max-w-4xl mx-auto">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Already Own a Volkswagen?</h2>
                  <p className="text-gray-700">
                    Our shop handles everything from routine oil changes to full engine rebuilds. Same technicians. Same VW expertise. Check out our dedicated VW repair page for service details and pricing.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/services/european-car-repair/volkswagen"
                    className="inline-flex items-center gap-2 bg-[#001E50] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#002E70] transition"
                  >
                    VW Repair Services <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-[#001E50] to-[#003580] rounded-xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-4">Ready to Drive a Volkswagen Home?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Stop by the lot or give us a call. No pressure. Just cars and straight answers.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="tel:8173822316"
                className="bg-white text-[#001E50] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
              >
                Call (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
              >
                Contact Us
              </Link>
            </div>
            <p className="mt-6 text-blue-200">305 Co Rd 3470, Paradise, TX 76073 — Serving Dallas-Fort Worth</p>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default VolkswagenInventory;
